/**
 * `StoragePort` su `localStorage`.
 *
 * **`secureBacking` dice la verità: `plain_local_storage`.** Sul prototipo era
 * `localStorage`; sull'apparecchio vero è l'archivio sicuro del sistema — Keystore su
 * Android, che arriva con l'involucro (02-ACCESSO §5). Dichiararlo invece di lasciarlo
 * intendere è il punto: un'interfaccia che deve dire «le tue credenziali sono protette»
 * può interrogare questo campo e, qui, non dirlo.
 *
 * Le credenziali **sono** la sessione e viaggiano in query string a ogni richiesta: per
 * questo vanno nell'archivio sicuro e non in un file qualunque (01-ARCHITETTURA §4).
 */

import type { StoragePort } from '@player/ports';
// I nomi delle chiavi dal dominio, non ribattuti: vedi `CARRIED_OVER`.
import { CACHE_KEY_PREFIX, CACHE_KEYS } from '@player/domain';
import { createLocalStore, type KeyValueStore } from './indexedDbStore.js';

export { createLocalStore, type KeyValueStore } from './indexedDbStore.js';

export const STORAGE_KEYS = {
  /** Le credenziali conservate. Si cancellano solo uscendo, o quando il server le
   *  rifiuta davvero (02-ACCESSO §4.2, regola 3). */
  credentials: 'player.credentials.v1',
  /** UUID generato al primo avvio. Non autentica niente: serve a contare le connessioni
   *  e a diagnosticare (02-ACCESSO §4.2). */
  deviceId: 'player.deviceId.v1',
  /** Impostazioni di prestazione e lingua: sono dell'apparecchio, non della persona. */
  settings: 'player.settings.v1',
  /** PIN adulti. Locale, e **non si cancella uscendo**: altrimenti uscire e rientrare
   *  sarebbe il modo di aggirare il controllo genitori (02-ACCESSO §7). */
  parentalPin: 'player.pin.v1',
  catalogEtag: 'player.catalog.etag.v1',
} as const;

/**
 * Quel che si travasa da `localStorage` a IndexedDB al primo avvio.
 *
 * **Solo l'irrecuperabile.** Sotto lo stesso prefisso convivevano due cose molto diverse: i listini,
 * che si riscaricano con una richiesta, e questi, che non stanno da nessun'altra parte — non sul
 * gateway, non su `line.app_data`, non su TMDB. Ripartire puliti costerebbe a chi ha già l'app i
 * preferiti, il segno di dov'era arrivato a guardare e la sua lista: roba costruita a mano, una
 * stella per volta.
 *
 * ## I nomi si importano, non si ribattono
 *
 * Li avevo ribattuti a mano come `player.cache.favorites`, e il prefisso vero è `palco.cache.`:
 * l'elenco non avrebbe travasato niente e non avrebbe cancellato niente — un travaso che gira, non
 * solleva, e non fa nulla, cioè il modo di fallire che non lascia traccia. L'ha trovato la prova, non
 * la lettura.
 */
const CARRIED_OVER: ReadonlyArray<string> = [
  CACHE_KEYS.favorites,
  CACHE_KEYS.lastChannel,
  CACHE_KEYS.progress,
  CACHE_KEYS.myList,
  /** I titoli aperti di recente: li ha prodotti il guardare, e il gateway non sa niente di loro. */
  CACHE_KEYS.recentVod,
  /*
    Non è avanzamento, ma non è nemmeno riscaricabile in blocco: si riempie una serie per volta,
    aprendone la scheda. Senza, «Continua a guardare» dimentica a che punto sono tutte le serie
    finché non le si riapre una a una.
  */
  CACHE_KEYS.seriesSeasons,
];

/*
  `recentSearches` resta fuori di proposito: si rifà da sé alla prima ricerca, e non vale la riga.
  I listini — canali, categorie, film, serie, EPG, schede — si cancellano: si riscaricano con una
  richiesta, e copiarli vorrebbe dire due verità sullo stesso dato con quella vecchia che occupa il
  tetto che tutto questo serve a liberare.
*/

export class WebStorage implements StoragePort {
  readonly secureBacking = 'plain_local_storage' as const;

  /**
   * Il travaso, fatto una volta e **aspettato da ogni lettura locale**.
   *
   * È una promessa tenuta qui, non un `await` in un costruttore — i costruttori non aspettano — e
   * nemmeno un effetto al montaggio: la prima lettura può arrivare prima di qualunque schermata, e
   * leggere mentre il travaso è a metà darebbe «nessun preferito» a chi ne ha trenta.
   */
  private readonly migration: Promise<void>;

  constructor(
    private readonly backing: Storage,
    private readonly local: KeyValueStore = createLocalStore(),
  ) {
    this.migration = this.migrate();
  }

  get localPersists(): boolean {
    return this.local.persists;
  }

  /**
   * Porta in IndexedDB quel che non si può riscaricare, e libera il resto.
   *
   * Non solleva mai. Un travaso che fallisce costa dei preferiti; un travaso che **impedisce
   * l'avvio** costa l'app, e il secondo prezzo non vale il primo. Quel che non riesce resta dov'è:
   * `localStorage` non viene svuotato se la scrittura non è andata a buon fine.
   */
  private async migrate(): Promise<void> {
    if (!this.local.persists) return;
    try {
      for (const key of CARRIED_OVER) {
        const value = this.backing.getItem(key);
        if (value === null) continue;
        // Non si sovrascrive quel che c'è già: al secondo avvio il valore buono è quello in
        // IndexedDB, e ricopiarci sopra la fotografia vecchia di `localStorage` cancellerebbe tutto
        // quel che si è fatto da allora.
        if ((await this.local.read(key)) === null) await this.local.write(key, value);
        this.backing.removeItem(key);
      }
      // I listini: si buttano e basta. Occupano il tetto e si riscaricano da soli.
      const doomed: string[] = [];
      for (let i = 0; i < this.backing.length; i += 1) {
        const key = this.backing.key(i);
        if (key !== null && key.startsWith(CACHE_KEY_PREFIX)) doomed.push(key);
      }
      for (const key of doomed) this.backing.removeItem(key);
    } catch {
      /* Vedi sopra: mai fermare l'avvio per un travaso. */
    }
  }

  private read(key: string): Promise<string | null> {
    // `getItem` solleva quando la memoria del browser è esaurita o negata (modalità
    // privata su alcune WebView). L'errore si propaga: un archivio che finge di aver
    // scritto è il modo in cui un utente si ritrova buttato fuori a ogni avvio.
    return Promise.resolve(this.backing.getItem(key));
  }

  private write(key: string, value: string): Promise<void> {
    this.backing.setItem(key, value);
    return Promise.resolve();
  }

  private remove(key: string): Promise<void> {
    this.backing.removeItem(key);
    return Promise.resolve();
  }

  readSecure(key: string): Promise<string | null> {
    return this.read(key);
  }

  writeSecure(key: string, value: string): Promise<void> {
    return this.write(key, value);
  }

  removeSecure(key: string): Promise<void> {
    return this.remove(key);
  }

  /*
    La metà locale non passa più da `localStorage`.

    Ognuna aspetta il travaso: leggere mentre è a metà darebbe «nessun preferito» a chi ne ha trenta,
    e scrivere prima darebbe un valore che il travaso poi non sovrascrive — ma nell'ordine sbagliato.
  */
  async readLocal(key: string): Promise<string | null> {
    await this.migration;
    return this.local.read(key);
  }

  async writeLocal(key: string, value: string): Promise<void> {
    await this.migration;
    return this.local.write(key, value);
  }

  async removeLocal(key: string): Promise<void> {
    await this.migration;
    return this.local.remove(key);
  }

  async listLocal(prefix: string): Promise<ReadonlyArray<string>> {
    await this.migration;
    return this.local.list(prefix);
  }
}

/**
 * Legge il `device_id`, generandolo al primo avvio.
 *
 * `crypto.randomUUID` non c'è su ogni WebView congelata; quando manca si usa
 * `crypto.getRandomValues`, che c'è da molto più tempo. Se manca anche quello si
 * **solleva**: un identificativo prevedibile non è un identificativo, e inventarlo con
 * `Math.random` sarebbe un ripiego silenzioso.
 */
export async function ensureDeviceId(storage: StoragePort): Promise<string> {
  /*
    `Secure` e non `Local`, e la differenza non è teorica.

    Questa chiave vive in `localStorage` come `player.deviceId.v1` da sempre. Quando la metà locale è
    passata a IndexedDB, `readLocal` ha smesso di guardare lì: ogni apparecchio già installato non
    avrebbe trovato il proprio identificativo e se ne sarebbe **generato uno nuovo**, presentandosi al
    gateway come un apparecchio mai visto. Contro un limite di connessioni per linea, è un cliente che
    smette di poter guardare senza che niente sia cambiato da lui.

    E indipendentemente dal travaso, `Secure` è la casa giusta: sta con le credenziali e le
    impostazioni — roba dell'apparecchio, minuscola, che deve esserci all'avvio — non con il catalogo,
    che è cache e si butta.
  */
  const existing = await storage.readSecure(STORAGE_KEYS.deviceId);
  if (existing && existing.length > 0) return existing;
  const generated = generateUuidV4();
  await storage.writeSecure(STORAGE_KEYS.deviceId, generated);
  return generated;
}

function generateUuidV4(): string {
  /*
    `window` e non `globalThis`: quest'ultimo è di Chromium 71, e Tizen 5 monta il 63.

    Non è un metodo che manca ed è `undefined` — è un nome che non esiste, quindi `ReferenceError`
    appena questa funzione gira. E gira alla **prima apertura**, per darsi un identificativo
    d'apparecchio: l'app risulterebbe impossibile da usare proprio dove non si può provare comodamente.
    Trovato dalla prova `tizen5.test.ts`, non da un collaudo.

    Il `typeof` davanti perché questo adattatore è per il browser ma il nome va scritto in modo che
    non esploda comunque altrove: un `ReferenceError` è peggio del ramo che solleva qui sotto, che
    almeno dice cosa manca.
  */
  const source = typeof window !== 'undefined' ? window.crypto : undefined;
  if (source && typeof source.randomUUID === 'function') return source.randomUUID();
  if (!source || typeof source.getRandomValues !== 'function') {
    throw new Error('ensureDeviceId: nessuna sorgente casuale crittografica disponibile');
  }
  const bytes = source.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return (
    `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
    `${hex.slice(16, 20)}-${hex.slice(20)}`
  );
}
